import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { Button, Col, Table, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';

import { backgroundGrey, grape, softBlueLight } from 'lib/css/colors';
import {
  BRANDS_PRODUCTS,
  GLOBAL_MANUFACTURERS_PRODUCTS,
  GLOBAL_MANUFACTURERS_FOR_DROPDOWN,
  GLOBAL_VENDORS_FOR_DROPDOWN,
} from 'lib/networking/endpoints';
import ChangeBrandModal from 'components/manage_brands/brand_details/ChangeBrandModal';
import Container from 'components/shared/Container';
import CopyableText from 'components/shared/CopyableText';
import { DEFAULT_PAGE_SIZE } from 'lib/constants';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PaginationComponent from 'components/shared/PaginationComponent';
import { ProductTypeDisplay } from 'lib/enums/ProductType';
import pointerOnHover from 'lib/css/pointerOnHover';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import useBrandCategoryFilterOptions from 'lib/hooks/useBrandCategoryFilterOptions';
import useGet from 'lib/hooks/useGet';
import { createGoogleSearchUrl } from 'lib/utils/webSearchUtils';
import { removeUndefinedAndNull } from 'lib/utils/objectUtils';

const DEFAULT_SELECTED_IDS = {
  manufacturerProducts: [],
  vendorProducts: [],
};

function ProductsView({ brandId, brandName, manufacturerId, globalVendorId }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(
    globalVendorId ? { value: globalVendorId, label: '' } : null,
  );
  const [selectedL1, setSelectedL1] = useState(null);
  const [selectedL2, setSelectedL2] = useState(null);
  const [selectedL3, setSelectedL3] = useState(null);
  const [selectedL4, setSelectedL4] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState(DEFAULT_SELECTED_IDS);
  const [selectAll, setSelectAll] = useState(false);
  const [showChangeBrandModal, setShowChangeBrandModal] = useState(false);
  const [selectAllOnAllPages, setSelectAllOnAllPages] = useState(false);

  const {
    data: { brand_products = [], brand_products_count = 0 } = {},
    loading: brandProductsLoading,
    refetch: refetchBrandProducts,
  } = useGet(
    BRANDS_PRODUCTS,
    removeUndefinedAndNull({
      brand_id: brandId,
      limit: selectAllOnAllPages ? undefined : itemsPerPage,
      offset: selectAllOnAllPages ? 0 : itemsPerPage * (currentPage - 1),
      manufacturer_id: selectedManufacturer ? selectedManufacturer.value : undefined,
      vendor_id: selectedVendor ? selectedVendor.value : undefined,
      l1_category_id: selectedL1 ? selectedL1.value : undefined,
      l2_category_id: selectedL2 ? selectedL2.value : undefined,
      l3_category_id: selectedL3 ? selectedL3.value : undefined,
      l4_category_id: selectedL4 ? selectedL4.value : undefined,
    }),
    !brandId,
  );

  const { options: categoryOptions, loading: categoryOptionsLoading } =
    useBrandCategoryFilterOptions(
      brandId,
      selectedManufacturer?.value,
      selectedVendor?.value,
      selectedL1?.value,
      selectedL2?.value,
      selectedL3?.value,
    );

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedManufacturer, selectedVendor, selectedL1, selectedL2, selectedL3, selectedL4]);

  const {
    data: { manufacturer_products = [], manufacturer_products_count = 0 } = {},
    loading: manufacturerActivityLoading,
    refetch: refetchManufacturerProducts,
  } = useGet(
    GLOBAL_MANUFACTURERS_PRODUCTS,
    {
      manufacturer_id: manufacturerId,
      limit: selectAllOnAllPages ? undefined : itemsPerPage,
      offset: selectAllOnAllPages ? 0 : itemsPerPage * (currentPage - 1),
    },
    !manufacturerId,
  );

  const products = useMemo(() => {
    if (brandId && brand_products.length) {
      return brand_products;
    } else if (manufacturerId && manufacturer_products.length) {
      return manufacturer_products;
    }
    return [];
  }, [brandId, manufacturerId, brand_products, manufacturer_products]);

  const count = useMemo(() => {
    if (brandId && brand_products_count) {
      return brand_products_count;
    } else if (manufacturerId && manufacturer_products_count) {
      return manufacturer_products_count;
    }
    return 0;
  }, [brandId, manufacturerId, brand_products_count, manufacturer_products_count]);

  useEffect(() => {
    if (selectAllOnAllPages && products.length > 0) {
      const manufacturerIds = [];
      const vendorIds = [];

      products.forEach(product => {
        if (product.entity_type === ProductTypeDisplay.MANUFACTURER) {
          manufacturerIds.push(product.id);
        } else {
          vendorIds.push(product.id);
        }
      });

      setSelectedProductIds({
        manufacturerProducts: manufacturerIds,
        vendorProducts: vendorIds,
      });
    } else if (!selectAllOnAllPages) {
      setSelectedProductIds(DEFAULT_SELECTED_IDS);
      setSelectAll(false);
    }
  }, [products, selectAllOnAllPages]);

  const { data: { data: globalVendors } = {} } = useGet(GLOBAL_VENDORS_FOR_DROPDOWN, {
    brand_id: brandId,
  });

  useEffect(() => {
    if (!selectedVendor?.value || selectedVendor.label || !globalVendors?.length) return;
    const match = globalVendors.find(v => v.id === selectedVendor.value);
    if (match) setSelectedVendor({ value: match.id, label: match.name });
  }, [globalVendors, selectedVendor]);

  const isProductSelected = product => {
    if (product.entity_type === ProductTypeDisplay.MANUFACTURER) {
      return selectedProductIds.manufacturerProducts.includes(product.id);
    } else {
      return selectedProductIds.vendorProducts.includes(product.id);
    }
  };

  const handleSelectAll = e => {
    const isChecked = e.target.checked;
    setSelectAll(isChecked);
    setSelectAllOnAllPages(false);

    if (isChecked) {
      const manufacturerIds = [];
      const vendorIds = [];

      products.forEach(product => {
        if (product.entity_type === ProductTypeDisplay.MANUFACTURER) {
          manufacturerIds.push(product.id);
        } else {
          vendorIds.push(product.id);
        }
      });

      setSelectedProductIds({
        manufacturerProducts: manufacturerIds,
        vendorProducts: vendorIds,
      });
    } else {
      setSelectedProductIds(DEFAULT_SELECTED_IDS);
    }
  };

  const handleSelectProduct = (product, isChecked) => {
    const { id, entity_type } = product;

    if (entity_type === ProductTypeDisplay.MANUFACTURER) {
      setSelectedProductIds(prev => ({
        ...prev,
        manufacturerProducts: isChecked
          ? [...prev.manufacturerProducts, id]
          : prev.manufacturerProducts.filter(pId => pId !== id),
      }));
    } else {
      setSelectedProductIds(prev => ({
        ...prev,
        vendorProducts: isChecked
          ? [...prev.vendorProducts, id]
          : prev.vendorProducts.filter(pId => pId !== id),
      }));
    }

    if (!isChecked) {
      setSelectAll(false);
    } else {
      const updatedSelection =
        entity_type === ProductTypeDisplay.MANUFACTURER
          ? {
              manufacturerProducts: [...selectedProductIds.manufacturerProducts, id],
              vendorProducts: [...selectedProductIds.vendorProducts],
            }
          : {
              manufacturerProducts: [...selectedProductIds.manufacturerProducts],
              vendorProducts: [...selectedProductIds.vendorProducts, id],
            };

      const allSelected = products.every(
        p =>
          (p.entity_type === ProductTypeDisplay.MANUFACTURER &&
            updatedSelection.manufacturerProducts.includes(p.id)) ||
          (p.entity_type !== ProductTypeDisplay.MANUFACTURER &&
            updatedSelection.vendorProducts.includes(p.id)),
      );

      setSelectAll(allSelected);
      setSelectAllOnAllPages(false);
    }
  };

  const handleRefetch = () => {
    if (brandId) {
      refetchBrandProducts();
    } else if (manufacturerId) {
      refetchManufacturerProducts();
    }
    setSelectedProductIds(DEFAULT_SELECTED_IDS);
  };

  const handleSelectAllOnAllPages = () => {
    setSelectAllOnAllPages(true);
    setCurrentPage(1);
    setItemsPerPage(null);
  };

  const totalSelectedCount =
    selectedProductIds.manufacturerProducts.length + selectedProductIds.vendorProducts.length;

  if (brandProductsLoading || manufacturerActivityLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      {brandId && (
        <div className="mb-3">
          <Row>
            <Col md={5}>
              <Form.Group>
                <Form.Label>Select Manufacturer</Form.Label>
                <SearchableScrollableSelect
                  onChange={option => {
                    setSelectedManufacturer(option);
                    setSelectedL1(null);
                    setSelectedL2(null);
                    setSelectedL3(null);
                    setSelectedL4(null);
                  }}
                  fetchUrl={GLOBAL_MANUFACTURERS_FOR_DROPDOWN}
                  optionMapper={manufacturer => ({
                    label: manufacturer.name,
                    value: manufacturer.id,
                  })}
                  initOption={selectedManufacturer}
                  autoFocus={false}
                  placeholder="Search manufacturer..."
                  disabled={selectedVendor}
                />
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group>
                <Form.Label>Select Distributor</Form.Label>
                <Select
                  value={selectedVendor}
                  options={globalVendors?.map(vendor => ({
                    value: vendor.id,
                    label: vendor.name,
                  }))}
                  onChange={e => {
                    setSelectedVendor(e);
                    setSelectedL1(null);
                    setSelectedL2(null);
                    setSelectedL3(null);
                    setSelectedL4(null);
                  }}
                  placeholder="Search distributor..."
                  isDisabled={selectedManufacturer}
                  isSearchable
                  isClearable
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Select L1 Category</Form.Label>
                <Select
                  value={selectedL1}
                  options={categoryOptions.l1Options}
                  onChange={option => {
                    setSelectedL1(option);
                    setSelectedL2(null);
                    setSelectedL3(null);
                    setSelectedL4(null);
                  }}
                  placeholder={categoryOptionsLoading ? 'Loading...' : 'Select L1 category...'}
                  isDisabled={categoryOptionsLoading}
                  isSearchable
                  isClearable
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Select L2 Category</Form.Label>
                <Select
                  value={selectedL2}
                  options={categoryOptions.l2Options}
                  onChange={option => {
                    setSelectedL2(option);
                    setSelectedL3(null);
                    setSelectedL4(null);
                  }}
                  placeholder={categoryOptionsLoading ? 'Loading...' : 'Select L2 category...'}
                  isDisabled={categoryOptionsLoading}
                  isSearchable
                  isClearable
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Select L3 Category</Form.Label>
                <Select
                  value={selectedL3}
                  options={categoryOptions.l3Options}
                  onChange={option => {
                    setSelectedL3(option);
                    setSelectedL4(null);
                  }}
                  placeholder={categoryOptionsLoading ? 'Loading...' : 'Select L3 category...'}
                  isDisabled={categoryOptionsLoading}
                  isSearchable
                  isClearable
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Select L4 Category</Form.Label>
                <Select
                  value={selectedL4}
                  options={categoryOptions.l4Options}
                  onChange={option => setSelectedL4(option)}
                  placeholder={categoryOptionsLoading ? 'Loading...' : 'Select L4 category...'}
                  isDisabled={categoryOptionsLoading}
                  isSearchable
                  isClearable
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      )}
      <div className="my-2 d-flex justify-content-between align-items-center">
        <div>
          <p className="mb-0">{count} products found</p>
          {totalSelectedCount > 0 && (
            <div className="d-flex align-items-center">
              <p className="text-primary mb-0 mr-3">{totalSelectedCount} products selected</p>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowChangeBrandModal(true)}
                className="ml-2"
              >
                Manage Brand
              </Button>
              <Button
                className="ml-2"
                variant="primary"
                onClick={handleSelectAllOnAllPages}
                disabled={selectAllOnAllPages}
              >
                Select All on All Pages
              </Button>
            </div>
          )}
        </div>
        {!selectAllOnAllPages && (
          <PaginationComponent
            totalItems={count}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            defaultSize
          />
        )}
      </div>
      <Table className={css(styles.tableText)}>
        <thead className={css(styles.thead)}>
          <tr>
            {brandId && (
              <th>
                <Form.Check type="checkbox" checked={selectAll} onChange={handleSelectAll} />
              </th>
            )}
            {brandId && <th>Entity Type</th>}
            {brandId && <th>Entity Name</th>}
            <th>SKU</th>
            <th>Product Name</th>
            {!brandId && <th>Brand Name</th>}
            <th>Pack Size</th>
            <th>Search</th>
          </tr>
        </thead>
        <tbody className={css(styles.tbody)}>
          {products?.length ? (
            products.map((product, index) => (
              <tr
                key={index}
                className={classNames(
                  pointerOnHover,
                  css(isProductSelected(product) && styles.selectedRow, styles.hoverRow),
                )}
                onClick={() =>
                  window.open(
                    `/global-product-detail/${product.global_product_id}/` +
                      `${product.id}/${product.entity_type?.toLowerCase()}`,
                    '_blank',
                  )
                }
              >
                {brandId && (
                  <td onClick={e => e.stopPropagation()}>
                    <Form.Check
                      type="checkbox"
                      checked={isProductSelected(product)}
                      onChange={e => handleSelectProduct(product, e.target.checked)}
                    />
                  </td>
                )}
                {brandId && <td>{product.entity_type}</td>}
                {brandId && <td>{product.entity_name}</td>}
                <td>
                  <CopyableText text={product.sku || ''} tooltipText="Copy SKU" />
                </td>
                <td>
                  <CopyableText text={product.name || ''} tooltipText="Copy name" />
                </td>
                {!brandId && (
                  <td>
                    {product.brand_name ? (
                      <CopyableText text={product.brand_name} tooltipText="Copy brand name" />
                    ) : (
                      '-'
                    )}
                  </td>
                )}
                <td>
                  {product.pack_size ? (
                    <CopyableText text={product.pack_size} tooltipText="Copy pack size" />
                  ) : (
                    '-'
                  )}
                </td>
                <td onClick={e => e.stopPropagation()}>
                  <OverlayTrigger overlay={<Tooltip>Search Product on Google</Tooltip>}>
                    <FontAwesomeIcon
                      icon={faSearch}
                      className={pointerOnHover}
                      onClick={() =>
                        window.open(
                          createGoogleSearchUrl(
                            [product.name, 'by', brandName || product.brand_name],
                            false,
                          ),
                          '_blank',
                        )
                      }
                    />
                  </OverlayTrigger>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="100%" className="text-center">
                No products found.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {showChangeBrandModal && (
        <ChangeBrandModal
          selectedProductsList={selectedProductIds}
          onHide={() => setShowChangeBrandModal(false)}
          refetch={handleRefetch}
        />
      )}
    </Container>
  );
}

const styles = StyleSheet.create({
  tableText: {
    fontSize: '0.95rem',
  },
  thead: {
    position: 'sticky-top',
    zIndex: 1,
    backgroundColor: backgroundGrey,
  },
  tbody: {
    zIndex: 0,
    position: 'relative',
  },
  hoverRow: {
    ':hover': {
      backgroundColor: `${grape}10`,
    },
  },
  selectedRow: {
    backgroundColor: softBlueLight,
  },
});

ProductsView.propTypes = {
  brandId: PropTypes.number,
  brandName: PropTypes.string,
  manufacturerId: PropTypes.number,
  globalVendorId: PropTypes.number,
};

export default ProductsView;
